import NextLink from 'next/link';
import { type JSX } from 'react';

import cx from 'classnames';

import { type Masked } from '~/contentful/graphql';
import { type AnalyticsModule, type CardEvent, Event } from '~/ui/components/analytics';
import { Button, ButtonSize, ButtonVariant } from '~/ui/components/button';
import { FullBleedCTAScope, FullBleedCTATrigger } from '~/ui/components/fullBleedCta';
import { Link } from '~/ui/components/link';
import { HeroAsset } from '~/ui/modules/heroAsset';
import { type Hero_HeroAssetFragment } from '~/ui/modules/heroAsset/query';
import { Image } from '~/ui/modules/image';
import { getImageAspectRatio } from '~/ui/modules/image/getImageAspectRatio';
import { type Image_AssetFragment } from '~/ui/modules/image/query';
import truncateStyles from '~/ui/styles/truncate.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { getOrientation } from '~/ui/utils/aspectRatio';

import { CardLogo } from './CardLogo';
import styles from './base.module.scss';

/**
 * @property MINIMAL No logo or image. Used for related items carousels like "More annual reports" or "More grants to xyz"
 * @property COLLAPSED Uses logo or Mellon M if none is provided. Used in news carousels where there a mix of internal and external news
 * @property DEFAULT Includes image or logo, depending on what is provided
 * @property EMPHASIZED Large card that spans the whole page. Should not be rendered within a content grid
 */
export enum CardSize {
  /** See {@link CardSize} for description */
  MINIMAL = 'MINIMAL',
  /** See {@link CardSize} for description */
  COLLAPSED = 'COLLAPSED',
  /** See {@link CardSize} for description */
  DEFAULT = 'DEFAULT',
  /** See {@link CardSize} for description */
  EMPHASIZED = 'EMPHASIZED',
}

const truncationClasses = {
  1: truncateStyles.truncate1,
  2: truncateStyles.truncate2,
  3: truncateStyles.truncate3,
  4: truncateStyles.truncate4,
  6: truncateStyles.truncate6,
} as const;

/* Props that can be passed to `BaseCard` but aren't used directly */
type InheritedCardProps = Omit<React.ComponentPropsWithoutRef<'article'>, 'children'>;

export interface BaseCardProps extends InheritedCardProps {
  eyebrow?: string;
  title: string;
  isTitleLarge?: boolean;
  disableMaxHeight?: boolean;
  description?: string;
  cta: string;
  href: string;
  ctaAsButton?: boolean;
  secondaryCta?: string;
  secondaryHref?: string;
  event?: Omit<CardEvent, 'cardTitle' | 'cardCTA'>;
  truncateTitle?: keyof typeof truncationClasses;
  hero?: Masked<typeof Image_AssetFragment | typeof Hero_HeroAssetFragment>;
  logo?: Masked<typeof Image_AssetFragment>;
  /**
   * Determines what image/logo to render
   * @see CardSize
   */
  size: CardSize;
  middleSlot?: JSX.Element;
}

/* Most cards that render `BaseCard` allow `InheritedCardProps` and an `AnalyticsModule` to pass through */
export interface CommonCardProps extends InheritedCardProps {
  module?: AnalyticsModule;
}

export function BaseCard({
  eyebrow,
  title,
  description,
  cta,
  href,
  ctaAsButton = false,
  secondaryCta,
  secondaryHref,
  event,
  truncateTitle,
  hero: heroData,
  logo,
  size,
  middleSlot,
  isTitleLarge = false,
  disableMaxHeight = false,
  className,
  ...props
}: BaseCardProps) {
  let resolvedHero: typeof heroData = undefined;
  // `true` here indicates that we should render a Mellon logo
  let resolvedLogo: typeof logo | true = undefined;
  switch (size) {
    case CardSize.EMPHASIZED:
    case CardSize.DEFAULT:
      if (heroData) {
        resolvedHero = heroData;
        break;
      }

    // fall through is intentional! If there is no image, we want to use the collapsed version of the card.
    case CardSize.COLLAPSED:
      resolvedLogo = logo || true;
      break;

    case CardSize.MINIMAL:
      break;
  }
  const heroAspectRatio = resolvedHero ? getImageAspectRatio(resolvedHero) : undefined;
  // TODO: This is only calculated on the server. Should we use a hook to update the aspect ratio once it renders on the DOM?
  const heroOrientation = heroAspectRatio ? getOrientation(heroAspectRatio) : undefined;

  const ctaOnClickEvent = {
    name: Event.CARD_CLICK,
    data: {
      cardTitle: title,
      cardCTA: cta,
      ...event,
    },
  };

  return (
    <FullBleedCTAScope>
      <article
        className={cx(
          styles.root,
          {
            [styles.noMaxHeight]: disableMaxHeight,
            [styles.emphasized]: size === CardSize.EMPHASIZED,
            [styles.portrait]: heroOrientation === 'portrait',
            [styles.hasSecondaryCta]: secondaryCta && secondaryHref,
          },
          className,
        )}
        {...props}
      >
        {resolvedHero && (
          <HeroAsset
            data={resolvedHero}
            videoWrapperClassName={styles.image}
            imageClassName={styles.image}
            imageProps={{
              aspectRatio: { portrait: '4/5', landscape: '3/2' },
            }}
          />
        )}

        <div className={styles.content}>
          {resolvedLogo === true ? (
            <div className={styles.logoContainer}>
              <CardLogo randomSeed={title} />
            </div>
          ) : resolvedLogo ? (
            <div className={styles.logoContainer}>
              <Image image={resolvedLogo} />
            </div>
          ) : null}

          <div className={cx(styles.textContent, isTitleLarge && styles.titleLarge)}>
            {eyebrow && <div className={typographyStyles.bodySmall}>{eyebrow}</div>}
            <div
              className={cx(
                isTitleLarge ? typographyStyles.modulesTitle : typographyStyles.bodyLarge,
                truncateTitle && truncationClasses[truncateTitle],
              )}
            >
              {title}
            </div>
            {description && (
              <div className={cx(typographyStyles.paragraph, truncateStyles.truncate3)}>
                {description}
              </div>
            )}
          </div>

          {middleSlot && <div className={styles.middleSlot}>{middleSlot}</div>}

          <div className={styles.ctaWrapper}>
            <FullBleedCTATrigger>
              {!ctaAsButton ? (
                <Link href={href} withIcon event={ctaOnClickEvent}>
                  {cta}
                </Link>
              ) : (
                <Button
                  size={ButtonSize.TINY}
                  variant={ButtonVariant.PRIMARY}
                  render={<NextLink href={href} />}
                  event={ctaOnClickEvent}
                >
                  {cta}
                </Button>
              )}
            </FullBleedCTATrigger>
            {secondaryCta && secondaryHref && (
              <Button
                size={ButtonSize.TINY}
                variant={ButtonVariant.SECONDARY_TRANSPARENT}
                render={<NextLink href={secondaryHref} />}
              >
                {secondaryCta}
              </Button>
            )}
          </div>
        </div>
      </article>
    </FullBleedCTAScope>
  );
}
